import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiService, addObjection, addFAQ } from '../services/apiService';
import { format } from 'date-fns';
import MainLayout from '../components/MainLayout';
import { Tabs } from '../components/ui/Tabs';
import { PageHeader } from '../components/ui/PageHeader';
import SaveInquiryModal from '../components/SaveInquiryModal';
import { CheckCircle as CheckCircleIcon, ThumbUp as ThumbUpIcon, ThumbDown as ThumbDownIcon } from '@mui/icons-material';
import { Popover } from '@mui/material';
import { useRoleAccess } from '../hooks/useRoleAccess';
import { formatFileSize } from '../utils/formatFileSize';
import CallInsightsTab from '../components/callInsights/CallInsightsTab';
import ParticipantsEditor from '../components/session/ParticipantsEditor';
import { buildParticipantRows } from '../components/session/participantRows';
import EntryFeedbackControl from '../components/session/EntryFeedbackControl';
import SessionFeedbackForm from '../components/session/SessionFeedbackForm';
import { entryFeedbackTarget, EntryFeedbackSource } from '../components/session/entryFeedbackTarget';
import type { CallInsightsAnalysis } from '../components/adherence/types';
import { getSpeakerLabel, getSpeakerColor, Participant } from '../config/participants';
import { ADD_TO_KNOWLEDGE_BASE_LABEL, getPlaybookNameLabel } from '../constants/labels';
import { ANSWER_LABEL_CLASS } from '../constants/timelineCard';
import { Button } from '../components/ui/Button';
import { useConfirm } from '../components/ui/ConfirmDialog';
import { JoinedCallTag } from '../components/sessions/JoinedCallTag';
import { CO_SELLER_CALL_COPY } from '../constants/coSellerCalls';
import { ownerSpeechRateAlertCount, ownerSpeechRateAlerts, SpeechRateOwnerScope } from '../utils/speechRateAlerts';

interface TranscriptEntry {
  id: string;
  text: string;
  speaker: 'customer' | 'salesperson';
  timestamp: string;
  participantId?: string;
  speakerName?: string;
  /** Set on lines an automatic merge moved (standalone calls): the participant they came from. */
  autoMergedFrom?: string;
}

interface EntryFeedback {
  rating: 'up' | 'down';
  reason?: string;
  submittedAt?: string;
}

interface ConversationEntry {
  question: string;
  answer?: string;
  stored_answer?: string;  // Answer from vector store database
  category: string;
  relevance_score: number;
  timestamp: string;
  status: string;
  isProcessed?: boolean;
  feedback?: EntryFeedback;
}

interface SentimentEntry {
  transcript: string;
  speaker: string;
  dominant_emotion: string;
  confidence: number;
  emotions: Array<{
    emotion: string;
    confidence: number;
  }>;
  timestamp: string;
}

interface SpeechRateAlert {
  total_words: number;
  words_per_minute: number;
  timestamp: string;
  /** The rep whose rate it was; absent on legacy alerts (the owner's). */
  participantId?: string | number | null;
}

interface SessionDetailApiResponse {
  success: boolean;
  data?: SessionDetailData;
  message?: string;
  error?: string;
}

interface LandmineDetection {
  trigger: string;
  matched_text: string;
  category: string;
  priority: string;
  timestamp: string;
  speaker: string;
  /** What entry-feedback matches a landmine by; absent on older sessions (no feedback control). */
  detected_at?: string;
  feedback?: EntryFeedback;
}

interface CompetitorDetection {
  competitor: string;
  matched_text: string;
  confidence: number;
  timestamp: string;
  speaker: string;
  /** What entry-feedback matches a competitor by; absent on older sessions (no feedback control). */
  detected_at?: string;
  feedback?: EntryFeedback;
}

// Items within a role's tracking arrays
interface SalesPlaybookQuestionAsked {
  questionId: string;
  questionText: string;
  tags: string[];
  askedAt: string;
}

interface SalesPlaybookQuestionNote {
  questionId: string;
  questionText: string;
  note: string;
  timestamp: string;
}

interface SalesPlaybookPitchUsed {
  pitchId: string;
  pitchText: string;
  usedAt: string;
}

interface SalesPlaybookPitchNote {
  pitchId: string;
  pitchText: string;
  note: string;
  timestamp: string;
}

interface SalesPlaybookAdvancedUsed {
  advancedId: string;
  advancedText: string;
  usedAt: string;
}

interface SalesPlaybookAdvancedNote {
  advancedId: string;
  advancedText: string;
  note: string;
  timestamp: string;
}

// Each playbook/role combination used during the session.
// Keys follow the renamed salesPlaybookUsage schema (backend normalizes
// legacy documents server-side while its read-fallback flag is on).
interface SalesPlaybookRoleUsed {
  playbookName: string;
  playbookDisplayName: string;
  role: string;
  roleDisplayName: string;
  questionsAsked: SalesPlaybookQuestionAsked[];
  questionNotes: SalesPlaybookQuestionNote[];
  pitchesUsed: SalesPlaybookPitchUsed[];
  pitchNotes: SalesPlaybookPitchNote[];
  advancedUsed: SalesPlaybookAdvancedUsed[];
  advancedNotes: SalesPlaybookAdvancedNote[];
}

interface SalesPlaybookUsage {
  // Current/last selection
  selectedPlaybookName?: string;
  selectedPlaybookDisplayName?: string;
  selectedRole?: string;
  selectedRoleDisplayName?: string;
  totalQuestionsAvailable?: number;
  // All playbook/role combinations used during the session
  rolesUsed?: SalesPlaybookRoleUsed[];
}

interface SessionDetailData {
  _id: string;
  sessionId: string;
  sessionName: string;
  userId: string;
  userEmail: string;
  callStartTime: string;
  callEndTime: string;
  callDuration: number;
  transcripts: TranscriptEntry[];
  detectedObjections: ConversationEntry[];
  detectedFAQs: ConversationEntry[];
  newInquiries: ConversationEntry[];
  sentimentAnalysis: {
    entries: SentimentEntry[];
    sessionSummary?: {
      total_transcripts: number;
      emotion_distribution: Record<string, number>;
      average_confidence: number;
      session_dominant_emotion: string;
      session_confidence: number;
    };
  };
  speechRateAnalysis: {
    alerts: SpeechRateAlert[];
    totalAlerts?: number;
    averageWordsPerMinute?: number;
    wpm_threshold?: number;
    // Legacy, on sessions recorded before 2026-09-09.
    max_words?: number;
    time_window?: number;
  };
  landmineDetections?: LandmineDetection[];
  competitorDetections?: CompetitorDetection[];
  salesPlaybookUsage?: SalesPlaybookUsage;
  sessionFeedback?: Array<{
    comment: string;
    submittedAt: string;
    attachments: Array<{
      filename: string;
      originalName: string;
      cloudFrontUrl: string;
      fileSize: number;
      mimeType: string;
      uploadedAt: string;
    }>;
  }>;
  metadata: {
    roomName?: string;
    clientId?: string;
    /** 'calendar' | 'manual' (bot calls) | 'standalone_desktop' (desktop app). */
    source?: string;
  };
  // Session-level roster (multi-party): who was on the call, by participantId.
  // Absent on legacy sessions recorded before multi-party support.
  participants?: Participant[];
  statistics: any;
  status: string;
  callInsights?: CallInsightsAnalysis;
  /** The viewer joined this call with their own mic (co-seller): read-only, no grade (the backend leaves it out). */
  joinedAsCoSeller?: boolean;
  ownerName?: string | null;
}

type SessionDetailTab = 'call-insights' | 'timeline' | 'statistics' | 'salesperson-feedback';

const SESSION_DETAIL_TABS: { id: SessionDetailTab; label: string }[] = [
  { id: 'call-insights', label: 'Call Insights' },
  { id: 'timeline', label: 'Timeline' },
  { id: 'statistics', label: 'Statistics' },
  { id: 'salesperson-feedback', label: 'Salesperson Feedback' },
];

/** The owner's grade, coaching and feedback: never shown on a call the viewer joined as a co-seller. */
const OWNER_ONLY_TABS: ReadonlySet<SessionDetailTab> = new Set<SessionDetailTab>(['call-insights', 'salesperson-feedback']);
const CO_SELLER_DEFAULT_TAB: SessionDetailTab = 'timeline';

/** What decides whose speech-rate alerts are the owner's (utils/speechRateAlerts.ts). */
const speechRateOwnerScopeOf = (session: SessionDetailData): SpeechRateOwnerScope => ({
  source: session?.metadata?.source,
  userId: session?.userId,
  participants: session?.participants,
});

// One neutral chip for every detection category. The label carries the
// meaning; a hue per category was decoration, not status.
const CATEGORY_CHIP_CLASS = 'bg-surface-sunken text-ink-secondary';

// Read-only feedback indicator: what everyone but the session's owner sees
interface FeedbackBadgeProps {
  feedback?: { rating: 'up' | 'down'; reason?: string; submittedAt?: string };
}

const FeedbackBadge: React.FC<FeedbackBadgeProps> = ({ feedback }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  if (!feedback?.rating) return null;

  const isUp = feedback.rating === 'up';

  return (
    <>
      <span
        onMouseEnter={e => setAnchorEl(e.currentTarget)}
        onMouseLeave={() => setAnchorEl(null)}
        className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium border cursor-default ${
          isUp
            ? 'text-success border-success bg-success-tint'
            : 'text-error border-error bg-error-tint'
        }`}
      >
        {isUp
          ? <ThumbUpIcon style={{ fontSize: 12 }} />
          : <ThumbDownIcon style={{ fontSize: 12 }} />}
        {isUp ? 'Helpful' : 'Not helpful'}
      </span>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        disableRestoreFocus
        sx={{ pointerEvents: 'none' }}
      >
        <div className="p-3 max-w-xs text-sm">
          <p className={`font-semibold mb-1 ${isUp ? 'text-success' : 'text-error'}`}>
            {isUp ? 'Marked helpful' : 'Marked not helpful'}
          </p>
          <p className="text-ink-secondary text-xs">
            {feedback.reason || 'No reason provided'}
          </p>
          {feedback.submittedAt && (
            <p className="text-ink-muted text-xs mt-1">
              {format(new Date(feedback.submittedAt), 'PPp')}
            </p>
          )}
        </div>
      </Popover>
    </>
  );
};

const SessionDetail: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const { isAdmin, currentUserUid } = useRoleAccess();
  const confirm = useConfirm();
  const [session, setSession] = useState<SessionDetailData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<SessionDetailTab>('call-insights');
  const [scrollToTimestamp, setScrollToTimestamp] = useState<string | null>(null);
  const highlightTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [savingInquiry, setSavingInquiry] = useState<string | null>(null);
  const [showSaveModal, setShowSaveModal] = useState<ConversationEntry | null>(null);
  const [processedInquiries, setProcessedInquiries] = useState<Set<string>>(new Set());
  const [sessionFeedbackSubmitting, setSessionFeedbackSubmitting] = useState(false);
  const [sessionFeedbackError, setSessionFeedbackError] = useState<string | null>(null);
  // The session feedback form stays closed until the owner asks for it, and
  // closes again after a successful submit (owner, 2026-10-26).
  const [showSessionFeedbackForm, setShowSessionFeedbackForm] = useState(false);
  const isCoSellerView = session?.joinedAsCoSeller === true;
  // Both feedback routes write only where {sessionId, userId: caller} matches,
  // so only the session's own rep gets the controls; everyone else reads.
  const canGiveFeedback = !!currentUserUid && currentUserUid === session?.userId && !isCoSellerView;

  const fetchSessionDetail = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      setError(null);

      const response = await apiService.get(`/admin/sessions/${sessionId}`) as SessionDetailApiResponse;

      console.log('Session Detail API Response:', response); // Debug log

      // The response is the full response object from backend
      const detail = response?.data;
      if (response?.success && detail) {
        setSession(detail);
        if (detail?.joinedAsCoSeller) setActiveTab(CO_SELLER_DEFAULT_TAB);

        // Initialize processedInquiries from session data
        if (detail.newInquiries) {
          const processed = new Set<string>();
          detail.newInquiries.forEach((inq: ConversationEntry) => {
            if (inq.isProcessed) {
              processed.add(inq.question);
            }
          });
          setProcessedInquiries(processed);
        }
      }
    } catch (err: any) {
      console.error('Failed to fetch session detail:', err);
      setError(err.message || 'Failed to load session details');
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    if (sessionId) {
      fetchSessionDetail();
    }
  }, [sessionId, fetchSessionDetail]);

  const handleNavigateToTimestamp = useCallback((ts: string) => {
    setActiveTab('timeline');
    setScrollToTimestamp(ts);
  }, []);

  useEffect(() => {
    if (activeTab !== 'timeline' || !scrollToTimestamp) return;

    const targetMs = new Date(scrollToTimestamp).getTime();
    if (isNaN(targetMs)) {
      setScrollToTimestamp(null);
      return;
    }

    // Give the timeline a tick to render before scanning for data-ts elements.
    const timerId = setTimeout(() => {
      const elements = document.querySelectorAll<HTMLElement>('[data-ts]');
      let closest: HTMLElement | null = null;
      let minDiff = Infinity;
      elements.forEach((el) => {
        const diff = Math.abs(new Date(el.dataset.ts!).getTime() - targetMs);
        if (diff < minDiff) {
          minDiff = diff;
          closest = el;
        }
      });

      if (closest) {
        (closest as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'center' });
        (closest as HTMLElement).classList.add('ring-2', 'ring-signature', 'ring-offset-2', 'transition-all');
        if (highlightTimeoutRef.current) clearTimeout(highlightTimeoutRef.current);
        highlightTimeoutRef.current = setTimeout(() => {
          (closest as HTMLElement).classList.remove('ring-2', 'ring-signature', 'ring-offset-2', 'transition-all');
        }, 3000);
      }

      setScrollToTimestamp(null);
    }, 50);

    return () => clearTimeout(timerId);
  }, [activeTab, scrollToTimestamp]);

  const formatDuration = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}h ${minutes}m ${secs}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${secs}s`;
    } else {
      return `${secs}s`;
    }
  };

  const formatAnswerText = (text: string): React.ReactElement => {
    // Only split by actual newlines to preserve text integrity
    const lines = text.split('\n').filter(line => line.trim());
    
    return (
      <div className="space-y-1">
        {lines.map((line, index) => {
          const trimmedLine = line.trim();
          // Check if it's a bullet point or numbered item at the start of a line
          if (trimmedLine.match(/^[•\-*]/) || trimmedLine.match(/^\d+\./)) {
            return (
              <div key={index} className="flex items-start gap-2">
                <span className="text-ink-muted mt-0.5">•</span>
                <span className="flex-1">{trimmedLine.replace(/^[•\-*\d+.]\s*/, '')}</span>
              </div>
            );
          } else if (trimmedLine) {
            return <div key={index}>{trimmedLine}</div>;
          }
          return null;
        })}
      </div>
    );
  };

  // A participant edit rewrites lines, alerts and concerns server-side (and a
  // role change re-runs the analysis), so the whole session is reloaded.
  const handleParticipantsSaved = (message: string) => {
    setError(null);
    setSuccess(message);
    setTimeout(() => setSuccess(null), 6000);
    fetchSessionDetail(true);
  };

  // entry-feedback answers 200 {persisted:false} when nothing matched, so only
  // persisted:true counts; the new rating then comes from the re-fetch.
  const handleEntryFeedback = async (
    source: EntryFeedbackSource,
    item: ConversationEntry | LandmineDetection | CompetitorDetection,
    rating: 'up' | 'down',
    reason: string,
  ) => {
    const target = session ? entryFeedbackTarget(source, item) : null;
    if (!session || !target) throw new Error('This item cannot take feedback');
    const res = await apiService.submitEntryFeedback(session.sessionId, {
      ...target,
      rating,
      ...(reason ? { reason } : {}),
    });
    if (!(res?.success && res?.persisted === true)) {
      throw new Error('Feedback was not saved');
    }
    await fetchSessionDetail(true);
  };

  // general-feedback does not echo the entry (attachment URLs exist only
  // server-side), so the list is re-fetched after a save.
  const handleSessionFeedback = async (formData: FormData) => {
    if (!session) return;
    setSessionFeedbackSubmitting(true);
    setSessionFeedbackError(null);
    try {
      await apiService.submitSessionFeedback(session.sessionId, formData);
      setSuccess('Feedback submitted');
      setTimeout(() => setSuccess(null), 6000);
      setShowSessionFeedbackForm(false);
      await fetchSessionDetail(true);
    } catch (err: any) {
      setSessionFeedbackError(err?.message || 'Failed to submit feedback');
      throw err;
    } finally {
      setSessionFeedbackSubmitting(false);
    }
  };

  // The owner rates the item; everyone else sees the read-only badge.
  const renderEntryFeedback = (source: EntryFeedbackSource, item: any) => {
    if (canGiveFeedback && entryFeedbackTarget(source, item)) {
      return (
        <EntryFeedbackControl
          currentFeedback={item.feedback}
          onSubmit={(rating, reason) => handleEntryFeedback(source, item, rating, reason)}
        />
      );
    }
    return <FeedbackBadge feedback={item.feedback} />;
  };

  const renderTimelineView = () => {
    if (!session) return null;

    // Combine all events into a timeline
    const timelineEvents: any[] = [];

    // Add transcripts
    session.transcripts.forEach(transcript => {
      timelineEvents.push({
        type: 'transcript',
        timestamp: transcript.timestamp,
        data: transcript
      });
    });

    // Add detected questions/objections, tagged with the array each came from
    // (entry-feedback routes by it)
    const detections: Array<[EntryFeedbackSource, ConversationEntry[] | undefined]> = [
      ['objection', session.detectedObjections],
      ['faq', session.detectedFAQs],
      ['inquiry', session.newInquiries],
    ];
    detections.forEach(([source, entries]) => {
      (entries ?? []).forEach(entry => {
        timelineEvents.push({
          type: 'detection',
          source,
          timestamp: entry.timestamp,
          data: entry
        });
      });
    });

    // Add speech rate alerts
    session.speechRateAnalysis.alerts.forEach(alert => {
      timelineEvents.push({
        type: 'speechAlert',
        timestamp: alert.timestamp,
        data: alert
      });
    });

    // Add landmine detections
    if (session.landmineDetections) {
      session.landmineDetections.forEach(detection => {
        timelineEvents.push({
          type: 'landmine',
          source: 'landmine',
          timestamp: detection.timestamp,
          data: detection
        });
      });
    }

    // Add competitor detections
    if (session.competitorDetections) {
      session.competitorDetections.forEach(detection => {
        timelineEvents.push({
          type: 'competitor',
          source: 'competitor',
          timestamp: detection.timestamp,
          data: detection
        });
      });
    }

    // Sort by timestamp
    timelineEvents.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());

    return (
      <div className="space-y-4">
        {/* Who was on the call, editable (names and roles). Hidden for legacy
            sessions with no participant identity. */}
        <ParticipantsEditor
          sessionId={session.sessionId}
          transcripts={session.transcripts}
          participants={session.participants}
          sessionSource={session.metadata?.source}
          readOnly={isCoSellerView}
          onSaved={handleParticipantsSaved}
          onError={setError}
        />
        {timelineEvents.map((event, index) => {
          // Chat-style bubble for transcripts: salesperson on the right, customers
          // on the left, each customer in its own palette color (multi-party).
          // Legacy sessions with no roster fall back to plain role labels and the
          // first palette slot for each role.
          if (event.type === 'transcript') {
            const isSalesperson = event.data.speaker === 'salesperson';
            const color = getSpeakerColor(event.data, session?.participants);
            const label = getSpeakerLabel(event.data, session?.participants, {
              salespersonLabel: 'Salesperson',
            });
            return (
              <div key={index} data-ts={event.timestamp}
                   className={`flex ${isSalesperson ? 'justify-end' : 'justify-start'}`}>
                <div className="max-w-[78%] min-w-[120px]">
                  <div className={`flex items-center gap-2 mb-1 ${isSalesperson ? 'flex-row-reverse' : ''}`}>
                    <span className="text-xs font-semibold"
                          style={{ color: isSalesperson ? color.border : color.text }}>
                      {label}
                    </span>
                    <span className="text-[10px] text-ink-muted">
                      {format(new Date(event.timestamp), 'HH:mm:ss')}
                    </span>
                  </div>
                  <div className="px-3.5 py-2 rounded-2xl shadow-sm"
                       style={{
                         backgroundColor: color.bg,
                         color: color.text,
                         border: `1px solid ${color.border}`,
                         borderBottomRightRadius: isSalesperson ? 6 : undefined,
                         borderBottomLeftRadius: isSalesperson ? undefined : 6,
                       }}>
                    <div className="text-sm whitespace-pre-wrap">{event.data.text}</div>
                  </div>
                </div>
              </div>
            );
          }

          // Annotations (objections/FAQs, landmines, competitors, speech alerts)
          // stay as full-width info cards with a timestamp gutter.
          return (
          <div key={index} data-ts={event.timestamp} data-testid="timeline-card" className="flex gap-4">
            <div className="flex-shrink-0 w-24 text-xs text-ink-muted">
              {format(new Date(event.timestamp), 'HH:mm:ss')}
            </div>
            <div className="flex-grow">
              {event.type === 'detection' && (
                <div className="p-3 rounded-lg bg-surface-subtle border-l-4 border-ink-border">
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${CATEGORY_CHIP_CLASS}`}>
                        {event.data.category === 'New Inquiry' || event.data.category === 'NEW_INQUIRY' ? 'New Objection/FAQ' : event.data.category}
                      </span>
                      <span className="text-xs text-ink-muted">
                        Relevance: {(event.data.relevance_score * 100).toFixed(0)}%
                      </span>
                      {renderEntryFeedback(event.source, event.data)}
                    </div>
                    {/* Only show buttons for admin users */}
                    {isAdmin && (event.data.category === 'New Inquiry' || event.data.category === 'NEW_INQUIRY') && !event.data.isProcessed && !processedInquiries.has(event.data.question) && (
                      <div className="flex items-center gap-2">
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => handleMarkAsProcessed(event.data)}
                          disabled={savingInquiry === event.data.question}
                        >
                          Mark as Processed
                        </Button>
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => setShowSaveModal(event.data)}
                          disabled={savingInquiry === event.data.question}
                        >
                          {ADD_TO_KNOWLEDGE_BASE_LABEL}
                        </Button>
                      </div>
                    )}
                    {(event.data.isProcessed || processedInquiries.has(event.data.question)) && (
                      <span className="px-2 py-1 text-xs font-medium text-success bg-success-tint rounded-full">
                        Processed
                      </span>
                    )}
                  </div>
                  <div className="text-sm font-medium">{event.data.question}</div>

                  {/* Display Stored Answer and/or AI Answer */}
                  {(event.data.stored_answer || event.data.answer) && (
                    <div className="mt-2 text-sm text-ink-secondary">
                      {/* Stored Answer (from database) */}
                      {event.data.stored_answer && (
                        <div className="mb-2">
                          <div className={`${ANSWER_LABEL_CLASS} flex items-center gap-2`}>
                            <span>Stored answer (knowledge base):</span>
                          </div>
                          <div className="bg-surface-subtle p-2 rounded border border-hairline">
                            {formatAnswerText(event.data.stored_answer)}
                          </div>
                        </div>
                      )}

                      {/* Divider if both answers exist */}
                      {event.data.stored_answer && event.data.answer && (
                        <div className="my-3 border-t border-ink-border"></div>
                      )}

                      {/* AI Answer */}
                      {event.data.answer && (
                        <div>
                          <div className={`${ANSWER_LABEL_CLASS} flex items-center gap-2`}>
                            <span>AI generated answer:</span>
                          </div>
                          <div className="bg-info-tint p-2 rounded border border-info">
                            {formatAnswerText(event.data.answer)}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
              
              {event.type === 'speechAlert' && (
                <div className="p-3 rounded-lg bg-warning-tint border-l-4 border-warning">
                  <div className="text-xs font-semibold mb-1">
                    Speech rate alert
                    {/* Each rep's own alert, named: a co-seller's is never the owner's. */}
                    {event.data?.participantId != null && event.data.participantId !== '' && (
                      <span data-testid="speech-alert-rep">
                        {' \u00B7 '}
                        {getSpeakerLabel(
                          { speaker: 'salesperson', participantId: String(event.data.participantId) },
                          session?.participants,
                          { salespersonLabel: 'Salesperson' },
                        )}
                      </span>
                    )}
                  </div>
                  {/*
                    The parenthetical used to read "(N words in 30s)", which
                    restated the same measurement in units nobody set a policy
                    in, and hardcoded a fallback window that was often not the
                    one the session was graded against. The threshold it was
                    judged against is the useful second number.
                  */}
                  <div className="text-sm">
                    Speaking too fast: {event.data.words_per_minute} words/minute
                    {session.speechRateAnalysis?.wpm_threshold
                      ? ` (alert set at ${session.speechRateAnalysis.wpm_threshold} WPM)`
                      : ''}
                  </div>
                </div>
              )}

              {event.type === 'landmine' && (
                <div className={`p-3 rounded-lg border-l-4 ${
                  event.data.priority === 'high' ? 'bg-red-50 border-red-500' :
                  event.data.priority === 'medium' ? 'bg-orange-50 border-orange-400' :
                  'bg-yellow-50 border-yellow-400'
                }`}>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-semibold">Landmine detected</span>
                    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                      event.data.priority === 'high' ? 'bg-red-100 text-red-800' :
                      event.data.priority === 'medium' ? 'bg-orange-100 text-orange-800' :
                      'bg-yellow-100 text-yellow-800'
                    }`}>
                      {event.data.priority} priority
                    </span>
                    {renderEntryFeedback(event.source, event.data)}
                  </div>
                  <div className="text-sm font-medium">Trigger: "{event.data.trigger}"</div>
                  <div className="text-xs text-ink-secondary mt-1">
                    Category: {event.data.category} | Speaker: {event.data.speaker}
                  </div>
                </div>
              )}

              {event.type === 'competitor' && (
                <div className="p-3 rounded-lg bg-purple-50 border-l-4 border-purple-400">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-semibold">Competitor mentioned</span>
                    {renderEntryFeedback(event.source, event.data)}
                  </div>
                  <div className="text-sm font-medium">{event.data.competitor}</div>
                  <div className="text-xs text-ink-secondary mt-1">
                    Confidence: {(event.data.confidence * 100).toFixed(0)}% | Speaker: {event.data.speaker}
                  </div>
                </div>
              )}
            </div>
          </div>
          );
        })}
      </div>
    );
  };


  const renderSalespersonFeedbackView = () => {
    if (!session) return null;

    const entries = session.sessionFeedback ?? [];

    // The owner adds feedback here; the backend 404s anyone else. The form is
    // opened on demand so the tab leads with the feedback already given.
    const openForm = () => {
      setSessionFeedbackError(null);
      setShowSessionFeedbackForm(true);
    };
    const form = !canGiveFeedback ? null : showSessionFeedbackForm ? (
      <div className="space-y-2">
        <SessionFeedbackForm
          onSubmit={handleSessionFeedback}
          submitting={sessionFeedbackSubmitting}
          error={sessionFeedbackError}
        />
        <div className="flex justify-end">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setShowSessionFeedbackForm(false)}
            disabled={sessionFeedbackSubmitting}
          >
            Cancel
          </Button>
        </div>
      </div>
    ) : (
      <div className="flex justify-end">
        <Button variant="primary" size="sm" onClick={openForm}>
          Add Session Feedback
        </Button>
      </div>
    );

    if (entries.length === 0 && canGiveFeedback) {
      return (
        <div className="space-y-4">
          {form}
          <p className="text-sm text-ink-muted">No session feedback yet</p>
        </div>
      );
    }

    if (entries.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center py-16 text-ink-muted">
          <p className="text-lg font-medium">No session feedback submitted</p>
          <p className="text-sm mt-1">The sales rep did not submit any general feedback for this session.</p>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {form}
        <p className="text-sm text-ink-muted">{entries.length} feedback {entries.length === 1 ? 'entry' : 'entries'}</p>
        {entries.map((entry, idx) => (
          <div key={idx} className="border border-hairline rounded-lg p-4">
            <div className="mb-2">
              <span className="text-xs text-ink-muted">
                {format(new Date(entry.submittedAt), 'MMM dd, yyyy HH:mm')}
              </span>
            </div>
            <p className="text-sm text-ink whitespace-pre-wrap">{entry.comment}</p>
            {entry.attachments?.length > 0 && (
              <div className="mt-3 space-y-1">
                <p className="text-xs font-medium text-ink-muted mb-1">Attachments ({entry.attachments.length})</p>
                {entry.attachments.map((file, fileIdx) => (
                  <a
                    key={fileIdx}
                    href={file.cloudFrontUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-sm text-signature hover:text-signature-strong hover:underline"
                  >
                    <span className="truncate">{file.originalName}</span>
                    <span className="text-xs text-ink-muted shrink-0">({formatFileSize(file.fileSize)})</span>
                  </a>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  const renderStatisticsView = () => {
    if (!session) return null;

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg p-6 border border-hairline">
          <h3 className="heading-3 mb-4">Conversation Stats</h3>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-ink-secondary">Total Transcripts</span>
              <span className="font-semibold">{session.statistics?.totalTranscripts || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-ink-secondary">Customer Transcripts</span>
              <span className="font-semibold">{session.statistics?.customerTranscripts || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-ink-secondary">Salesperson Transcripts</span>
              <span className="font-semibold">{session.statistics?.salespersonTranscripts || 0}</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg p-6 border border-hairline">
          <h3 className="heading-3 mb-4">Questions & Objections</h3>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-ink-secondary">Total Objections</span>
              <span className="font-semibold text-ink">{session.statistics?.totalObjections || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-ink-secondary">Total FAQs</span>
              <span className="font-semibold text-ink">{session.statistics?.totalFAQs || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-ink-secondary">New Objection/FAQ</span>
              <span className="font-semibold text-ink">{session.statistics?.totalNewInquiries || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-ink-secondary">Response Rate</span>
              <span className="font-semibold">{session.statistics?.responseRate || 0}%</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg p-6 border border-hairline">
          <h3 className="heading-3 mb-4">Detections</h3>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-ink-secondary">Landmine Alerts</span>
              <span className="font-semibold text-ink">
                {session.statistics?.totalLandmineDetections || session.landmineDetections?.length || 0}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-ink-secondary">Competitor Mentions</span>
              <span className="font-semibold text-ink">
                {session.statistics?.totalCompetitorMentions || session.competitorDetections?.length || 0}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-ink-secondary">Speech Rate Alerts</span>
              <span className="font-semibold text-ink" data-testid="speech-rate-alert-count">
                {ownerSpeechRateAlertCount(session.speechRateAnalysis, speechRateOwnerScopeOf(session))}
              </span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg p-6 border border-hairline">
          <h3 className="heading-3 mb-4">Sales Playbook</h3>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-ink-secondary">{getPlaybookNameLabel()}</span>
              <span className="font-semibold text-ink">
                {session.salesPlaybookUsage?.selectedPlaybookDisplayName || '-'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-ink-secondary">Roles Used</span>
              <span className="font-semibold text-ink">
                {session.salesPlaybookUsage?.rolesUsed?.length || 0}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-ink-secondary">Questions Asked</span>
              <span className="font-semibold text-ink">
                {session.salesPlaybookUsage?.rolesUsed?.reduce((sum, r) => sum + (r.questionsAsked?.length || 0), 0) || 0}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-ink-secondary">Notes Taken</span>
              <span className="font-semibold text-ink">
                {session.salesPlaybookUsage?.rolesUsed?.reduce((sum, r) =>
                  sum + (r.questionNotes?.length || 0) + (r.pitchNotes?.length || 0) + (r.advancedNotes?.length || 0), 0) || 0}
              </span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg p-6 border border-hairline">
          <h3 className="heading-3 mb-4">Call Timing</h3>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-ink-secondary">Duration</span>
              <span className="font-semibold text-ink">
                {formatDuration(session.callDuration)}
              </span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-ink-muted">Started</span>
              <span className="text-ink-secondary">
                {format(new Date(session.callStartTime), 'HH:mm:ss')}
              </span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-ink-muted">Ended</span>
              <span className="text-ink-secondary">
                {format(new Date(session.callEndTime), 'HH:mm:ss')}
              </span>
            </div>
            <div className="flex justify-between pt-3 border-t border-hairline">
              <span className="text-ink-secondary">Room Name</span>
              <span className="font-semibold">{session.metadata?.roomName || '-'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-ink-secondary">Status</span>
              <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                session.status === 'completed'
                  ? 'bg-success-tint text-success'
                  : 'bg-warning-tint text-warning'
              }`}>
                {session.status}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  };


  // The "Mark as Processed" button: nothing is saved, so the admin confirms first.
  const handleMarkAsProcessed = async (inquiry: ConversationEntry) => {
    const confirmed = await confirm({
      title: 'Mark as processed without saving?',
      message: 'This will hide the inquiry from your unprocessed list, but it will NOT be saved to the FAQs or Objections knowledge base.',
      confirmLabel: 'Mark as processed',
      variant: 'destructive',
    });

    if (!confirmed) {
      return;
    }
    await markInquiryProcessed(inquiry);
  };

  // Also what the save modal calls once it has used an existing entry or added a
  // variation: that already saved, so asking "without saving?" would be wrong.
  const markInquiryProcessed = async (inquiry: ConversationEntry) => {
    try {
      // Mark as processed in the backend
      await apiService.put(`/admin/sessions/${sessionId}/inquiry-processed`, {
        question: inquiry.question
      });

      // Update UI
      setProcessedInquiries(prev => new Set([...prev, inquiry.question]));

      // Update session data
      if (session) {
        const updatedSession = { ...session };
        updatedSession.newInquiries = updatedSession.newInquiries.map(inq =>
          inq.question === inquiry.question ? { ...inq, isProcessed: true } : inq
        );
        setSession(updatedSession);
      }

      // Show success message
      setSuccess('Inquiry marked as processed');
      setTimeout(() => setSuccess(null), 8000); // Increased to 8 seconds

      // Scroll to top to show success message
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err: any) {
      console.error('Failed to mark as processed:', err);
      setError('Failed to mark inquiry as processed');
    }
  };

  const handleSaveNewInquiry = async (inquiry: ConversationEntry, type: 'objection' | 'faq', editedData: { question: string; answer: string; category: string }) => {
    try {
      setSavingInquiry(inquiry.question);

      // First, save to the dictionary (objection or FAQ)
      if (type === 'objection') {
        // Pass: question, answer, category (for topic), isOriginal=true, parentId=null
        await addObjection(editedData.question, editedData.answer, editedData.category, true, null);
      } else {
        // FAQs: question, answer, category (for topic)
        await addFAQ(editedData.question, editedData.answer, editedData.category);
      }

      // Show success message for saving to database
      setError(null);  // Clear any previous errors
      setSuccess(`Successfully added to the ${type === 'objection' ? 'objections' : 'FAQs'} knowledge base`);
      setTimeout(() => setSuccess(null), 10000); // Increased from 5 seconds to 10 seconds

      // Then, try to mark as processed in the backend
      try {
        console.log('[SessionDetail] Attempting to mark inquiry as processed');
        console.log('[SessionDetail] Session ID:', sessionId);
        console.log('[SessionDetail] Question being sent:', inquiry.question);

        const response = await apiService.put(`/admin/sessions/${sessionId}/inquiry-processed`, {
          question: inquiry.question
        });

        console.log('[SessionDetail] Mark as processed response:', response);

        // Only mark as processed in UI if backend call succeeds
        setProcessedInquiries(prev => new Set([...prev, inquiry.question]));

        // Update session data to mark as processed
        if (session) {
          const updatedSession = { ...session };
          updatedSession.newInquiries = updatedSession.newInquiries.map(inq =>
            inq.question === inquiry.question ? { ...inq, isProcessed: true } : inq
          );
          setSession(updatedSession);
        }

        console.log('[SessionDetail] Successfully marked as processed in UI');
      } catch (err: any) {
        console.error('[SessionDetail] Failed to persist processed state:', err);
        console.error('[SessionDetail] Error details:', {
          message: err.message,
          response: err.response,
          data: err.response?.data
        });
        // Update success message with warning (but don't fail the whole operation)
        setSuccess(prev => prev + ' (Note: could not mark as processed in session)');
      }

      setShowSaveModal(null);

      // Scroll to top to show success message
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error: any) {
      console.error('Failed to save inquiry:', error);
      alert(error.message || 'Failed to save inquiry to the knowledge base');
    } finally {
      setSavingInquiry(null);
    }
  };

  return (
    <MainLayout title={session?.sessionName || 'Session Details'}>
      <div className="p-2">
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-signature"></div>
          <p className="ml-3 text-ink-muted">Loading session details...</p>
        </div>
      ) : error || !session ? (
        <div>
          <div className="bg-error-tint border border-error text-error px-4 py-3 rounded-lg">
            {error || 'Session not found'}
          </div>
          <Button
            variant="primary"
            onClick={() => navigate('/sessions')}
            className="mt-4"
          >
            Back to Sessions
          </Button>
        </div>
      ) : (
        <>
      {/* Success/Error Messages */}
      {success && (
        <div className="mb-4 p-4 bg-success-tint border border-success rounded-lg flex items-center">
          <CheckCircleIcon className="text-success mr-2" style={{ fontSize: '1.25rem' }} />
          <span className="text-ink">{success}</span>
        </div>
      )}

      {/* Header */}
      <PageHeader
        title={session.sessionName}
        backLabel="Back to Sessions"
        onBack={() => navigate('/sessions')}
      />
      <div className="mb-3">
        <div className="bg-white rounded-lg shadow p-4">
          {isCoSellerView && (
            <div className="mb-3 flex flex-wrap items-center gap-2" data-testid="co-seller-view">
              <JoinedCallTag ownerName={session?.ownerName} />
              <span className="text-xs text-ink-muted">{CO_SELLER_CALL_COPY.readOnlyNote}</span>
            </div>
          )}
          <div className="flex flex-wrap gap-4 text-sm text-ink-secondary">
            <div>
              <span className="font-medium">User:</span> {session.userEmail}
            </div>
            <div>
              <span className="font-medium">Duration:</span>{' '}
              <span className="text-ink font-semibold">{formatDuration(session.callDuration)}</span>
            </div>
            <div>
              <span className="font-medium">Date:</span> {format(new Date(session.callStartTime), 'MMM dd, yyyy HH:mm')}
            </div>
            <div>
              <span className="font-medium">Room:</span> {session.metadata?.roomName || '-'}
            </div>
          </div>
        </div>
      </div>

      <Tabs
        idPrefix="session"
        ariaLabel="Session sections"
        value={activeTab}
        onChange={(id) => setActiveTab(id as SessionDetailTab)}
        items={isCoSellerView ? SESSION_DETAIL_TABS.filter((tab) => !OWNER_ONLY_TABS.has(tab.id)) : SESSION_DETAIL_TABS}
      />

      {/* Tab Content */}
      <div className="bg-white rounded-lg shadow p-4">
        {activeTab === 'call-insights' && !isCoSellerView && (
          <CallInsightsTab
            sessionId={session.sessionId}
            callInsights={session.callInsights}
            salesPlaybookUsage={session.salesPlaybookUsage}
            speechRateAlerts={ownerSpeechRateAlerts(session.speechRateAnalysis?.alerts, speechRateOwnerScopeOf(session))}
            isAdmin={isAdmin}
            onAnalysisUpdated={() => fetchSessionDetail(true)}
            onNavigateToTimestamp={handleNavigateToTimestamp}
            participantsEditor={
              buildParticipantRows(session.transcripts, session.participants).length > 0 ? (
                <ParticipantsEditor
                  sessionId={session.sessionId}
                  transcripts={session.transcripts}
                  participants={session.participants}
                  sessionSource={session.metadata?.source}
                  onSaved={handleParticipantsSaved}
                  onError={setError}
                />
              ) : undefined
            }
          />
        )}
        {activeTab === 'timeline' && renderTimelineView()}
        {activeTab === 'statistics' && renderStatisticsView()}
        {activeTab === 'salesperson-feedback' && !isCoSellerView && renderSalespersonFeedbackView()}
      </div>

      {/* Save New Inquiry Modal */}
      {showSaveModal && (
        <SaveInquiryModal
          inquiry={showSaveModal}
          onClose={() => setShowSaveModal(null)}
          onSave={handleSaveNewInquiry}
          onMarkAsProcessed={markInquiryProcessed}
          isSaving={savingInquiry === showSaveModal.question}
        />
      )}
        </>
      )}
      </div>
    </MainLayout>
  );
};

export default SessionDetail;